/**
 * Captures lightweight device and network metadata at the time of call.
 *
 * Used to annotate one-time Firestore events (e.g. form firstStartedAt)
 * so that support and engineering can retroactively correlate user-reported
 * issues with device type, viewport size, and network quality without
 * requiring the user to share browser dev-tools output.
 *
 * Design constraints:
 *   - NEVER throws — the entire body is wrapped in try/catch returning null
 *   - Every field falls back to null when the underlying API is unavailable
 *     (e.g. navigator.connection is Chromium-only; Safari and Firefox omit it)
 *   - Callers should treat a null return as "device context unavailable" and
 *     simply omit the field from the Firestore write
 *
 * @returns {{ userAgent: string|null, screenWidth: number|null,
 *             screenHeight: number|null, viewportWidth: number|null,
 *             viewportHeight: number|null, networkType: string|null,
 *             downlinkMbps: number|null } | null}
 */
export function getDeviceContext() {
  try {
    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    return {
      userAgent: navigator.userAgent || null,
      screenWidth: window.screen?.width ?? null,
      screenHeight: window.screen?.height ?? null,
      viewportWidth: window.innerWidth ?? null,
      viewportHeight: window.innerHeight ?? null,
      networkType: conn?.effectiveType || null,
      downlinkMbps: conn?.downlink ?? null,
    };
  } catch (_) {
    return null;
  }
}
